import React, { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, X } from 'lucide-react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import AdminFilters from '../components/filters/AdminFilters'
import AdminPagination from '../components/pagination/AdminPagination'
import { useAdminFilters } from '../hooks/useAdminFilters'
import CategoryForm from '../components/CategoryForm'
import ConfirmDialog from '../components/ConfirmDialog'
import Modal from '../components/common/Modal'
import { SkeletonLoader } from '../components/SkeletonLoader'
import { toTitleCase } from '../utils/stringHelpers'
import { extractErrorMessage } from '../utils/errorHandler'

interface Category {
  id: string
  name: string
  slug: string
  description?: string
  image?: string
  isActive: boolean
  displayOrder: number
}

interface PaginationData {
  page: number
  limit: number
  total: number
  pages: number
}

export default function CategoriesPage() {
  const filterManager = useAdminFilters({ defaultLimit: 20 })
  const { search, debouncedSearch, page, limit, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager

  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [pagination, setPagination] = useState<PaginationData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; categoryId: string | null; isLoading: boolean }>({
    isOpen: false,
    categoryId: null,
    isLoading: false,
  })

  useEffect(() => {
    fetchCategories()
  }, [page, limit, debouncedSearch, JSON.stringify(filterManager.filters)])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const response = await api.getCategories(page, limit, debouncedSearch)
      setCategories(response.data.data || [])
      setPagination(response.data.pagination)
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error)
      toast.error(errorMessage)
      console.error('Fetch categories error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddClick = () => {
    setEditingId(null)
    setEditingCategory(null)
    setShowModal(true)
  }

  const handleEditClick = async (category: Category) => {
    setEditingId(category.id)
    setEditingCategory(category)
    setShowModal(true)
  }

  const handleFormSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      if (editingId) {
        await api.updateCategory(editingId, formData)
        toast.success('Category updated successfully')
      } else {
        await api.createCategory(formData)
        toast.success('Category created successfully')
      }

      setShowModal(false)
      setEditingId(null)
      setEditingCategory(null)
      fetchCategories()
    } catch (error: any) {
      const message = extractErrorMessage(error)
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFormCancel = () => {
    setShowModal(false)
    setEditingId(null)
    setEditingCategory(null)
  }

  const handleDeleteClick = (id: string) => {
    setDeleteConfirm({ isOpen: true, categoryId: id, isLoading: false })
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.categoryId) return

    setDeleteConfirm((prev) => ({ ...prev, isLoading: true }))

    try {
      await api.deleteCategory(deleteConfirm.categoryId)
      toast.success('Category deleted successfully')
      setDeleteConfirm({ isOpen: false, categoryId: null, isLoading: false })
      fetchCategories()
    } catch (error: any) {
      const message = extractErrorMessage(error)
      toast.error(message)
      setDeleteConfirm((prev) => ({ ...prev, isLoading: false }))
    }
  }

  const handleCancelDelete = () => {
    setDeleteConfirm({ isOpen: false, categoryId: null, isLoading: false })
  }

  const handleToggleActive = async (category: Category) => {
    try {
      const formData = new FormData()
      formData.append('isActive', String(!category.isActive))
      await api.updateCategory(category.id, formData)
      toast.success(category.isActive ? 'Category disabled' : 'Category enabled')
      fetchCategories()
    } catch (error: any) {
      toast.error('Failed to update category')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">Categories</h1>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-2 bg-[#6D3A0D] text-white px-4 py-2 rounded-lg hover:bg-[#5D2A0D] transition"
        >
          <Plus size={20} />
          Add Category
        </button>
      </div>

      <AdminFilters
        search={search}
        searchPlaceholder="Search categories by name or slug..."
        onSearchChange={setSearch}
        filters={filterManager.filters}
        onFilterChange={setFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={loading}
      />

      {loading ? (
        <SkeletonLoader rows={3} cols={3} />
      ) : categories.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-[#F6EEE7]">
          <p className="text-gray-600">
            {hasActiveFilters ? 'No categories found matching your search.' : 'No categories found. Create one to get started!'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#6D3A0D] underline hover:text-[#5D2A0D]"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] shadow overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F6EEE7] border-b-2 border-[#F6EEE7]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F6EEE7]">
                {categories.map((category: Category) => (
                  <tr key={category.id} className="hover:bg-[#F6EEE7]/50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{toTitleCase(category.name)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{category.slug}</td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleToggleActive(category)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                          category.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {category.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{category.displayOrder}</td>
                    <td className="px-6 py-4 text-sm flex gap-3">
                      <button
                        onClick={() => handleEditClick(category)}
                        className="text-[#6D3A0D] hover:text-[#5D2A0D] font-semibold"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(category.id)}
                        className="text-red-600 hover:text-red-700 font-semibold"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.total > 0 && (
            <div className="mt-6">
              <AdminPagination
                page={page}
                limit={limit}
                total={pagination.total}
                onPageChange={setPage}
                onLimitChange={setLimit}
                isLoading={loading}
                showInfo={true}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleFormCancel}
        title={editingId ? 'Edit Category' : 'Add New Category'}
        maxWidth="2xl"
      >
        <CategoryForm
          initialData={editingCategory || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        isLoading={deleteConfirm.isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  )
}
